/**
 * Kachel „Termine“ – die Fristen eines Bezugs samt allem darunter (ADR 010).
 *
 * Steht in der Projektübersicht (ganzes Projekt), am Antrag (Antrag und seine Nachweise) und im
 * Nachweis-Detail. Abgeleitete und manuelle Fristen stehen zusammen; ein Klick auf eine
 * abgeleitete führt zu ihrem Bezug, eine manuelle öffnet sich im Dialog – dort wird sie
 * erledigt, bearbeitet und gelöscht. Erledigte bleiben ausgegraut stehen, damit sichtbar ist,
 * was schon abgehakt wurde.
 *
 * Daten und Änderungen kommen aus `useProjectDeadlines` der Seite – die Seite braucht dieselbe
 * Liste oft auch selbst (Rückfragen beim Löschen, „Nächster Termin“).
 *
 * Im Nachweis-Detail kommen die Fristen-Felder des Nachweises dazu (`fieldRows`), und die Kachel
 * zeigt zuerst nur, was gerade zählt: Fristen des aktuellen Status und offene manuelle Termine.
 * Fristen früherer Status und erledigte Termine klappen auf (`collapseFurther`).
 */
import React, { useCallback, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  AddOutlined as AddIcon,
  CheckCircleOutlineOutlined as DoneIcon,
  DeleteOutlined as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import Button from "../../common/inputs/Button";
import ConfirmDialog from "../../common/feedback/ConfirmDialog";
import DeadlineCell, { UrgencyIcon } from "../../common/display/DeadlineCell";
import ActionsMenu from "../../common/overlays/ActionsMenu";
import Section from "../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../tables/common/BaseTable";
import ManualDeadlineDialog from "./ManualDeadlineDialog";
import ScopeToggle from "./ScopeToggle";
import { useProjectDeadlines } from "../../../hooks/useProjectDeadlines";
import { DeadlineScope } from "../../../services/api/deadlines";
import { DeadlineItem } from "../../../types";
import { useToast } from "../../../contexts/ToastContext";
import { formatDate } from "../../../utils/formatters";
import {
  deadlinePath,
  isDone,
  isOnCurrentReference,
  referenceText,
} from "../../../utils/deadlines";
import { getUrgency } from "../../../utils/urgency";
import { colors, fonts } from "../../../styles/theme";
import NoteIndicator from "../../common/display/NoteIndicator";

type DeadlinesController = ReturnType<typeof useProjectDeadlines>;

interface DeadlinesSectionProps {
  projectId: string;
  controller: DeadlinesController;
  canEdit: boolean;
  /** Vorbelegter Bezug für „Termin anlegen“ – der Antrag bzw. Nachweis der Seite */
  permitProcedureId?: string | null;
  evidenceId?: string | null;
  /** Spalte „Bezug“ – entfällt im Nachweis-Detail, dort ist er immer derselbe */
  showReference?: boolean;
  /** Umschalter Meine/Team – in der Projektübersicht */
  withScopeToggle?: boolean;
  /** IAM-ID des angemeldeten Benutzers, für „Meine“ */
  currentUserId?: string;
  /** Ohne Abstand nach unten – wo die Seite die Kacheln selbst mit Abstand stapelt */
  noOuterPadding?: boolean;
  /**
   * Fristen aus Datumsfeldern des Bezugs (Nachweis): `current` gehört zum aktuellen Status,
   * `earlier` zu früheren. Ein Klick öffnet `onFieldRowClick` – das Feld pflegt der Aufrufer.
   */
  fieldRows?: { current: DeadlineItem[]; earlier: DeadlineItem[] };
  onFieldRowClick?: (deadline: DeadlineItem) => void;
  /** Nur Aktuelles zeigen, Früheres und Erledigtes hinter dem Pfeil */
  collapseFurther?: boolean;
}

const DeadlinesSection: React.FC<DeadlinesSectionProps> = ({
  projectId,
  controller,
  canEdit,
  permitProcedureId,
  evidenceId,
  showReference = true,
  withScopeToggle = false,
  currentUserId,
  noOuterPadding = false,
  fieldRows,
  onFieldRowClick,
  collapseFurther = false,
}) => {
  const navigate = useNavigate();
  const { deadlines, loading, update, setDone, remove, submit } = controller;
  const [scope, setScope] = useState<DeadlineScope>("team");
  const [creating, setCreating] = useState(false);
  /** Geöffnete manuelle Frist – zum Lesen; Bearbeiten im Dialog */
  const [selected, setSelected] = useState<DeadlineItem | null>(null);
  const [deleting, setDeleting] = useState<DeadlineItem | null>(null);
  const { showUndo } = useToast();
  const [showFurther, setShowFurther] = useState(false);

  const fieldIds = useMemo(
    () =>
      new Set(
        [...(fieldRows?.current ?? []), ...(fieldRows?.earlier ?? [])].map(
          (d) => d.id,
        ),
      ),
    [fieldRows],
  );
  const earlierIds = useMemo(
    () => new Set((fieldRows?.earlier ?? []).map((d) => d.id)),
    [fieldRows],
  );
  /** Gehört nicht zum aktuellen Stand: erledigt oder Frist eines früheren Status */
  const isFurther = useCallback(
    (d: DeadlineItem) => isDone(d) || earlierIds.has(d.id),
    [earlierIds],
  );

  const all = useMemo(
    () => [
      ...(fieldRows?.current ?? []),
      ...(fieldRows?.earlier ?? []),
      ...deadlines,
    ],
    [fieldRows, deadlines],
  );
  const furtherCount = collapseFurther ? all.filter(isFurther).length : 0;

  const visible = useMemo(
    () =>
      all
        .filter(
          (d) =>
            !(withScopeToggle && scope === "mine") ||
            d.verantwortlich?.external_id === currentUserId,
        )
        .filter((d) => !collapseFurther || showFurther || !isFurther(d)),
    [
      all,
      withScopeToggle,
      scope,
      currentUserId,
      collapseFurther,
      showFurther,
      isFurther,
    ],
  );

  const tableData = useMemo(
    () =>
      visible.map((d) => ({
        ...d,
        reference: referenceText(d),
        responsible:
          d.verantwortlich?.display_name || d.verantwortlich?.username || "",
        done: isDone(d) ? 1 : 0,
      })),
    [visible],
  );

  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: "bezeichnung",
        label: "Termin",
        align: "left",
        width: showReference ? "30%" : "45%",
        sortable: true,
        untertitel: (row) => (row as DeadlineItem).note || null,
      },
      ...(showReference
        ? [
            {
              key: "reference",
              label: "Bezug",
              align: "left" as const,
              width: "25%",
              sortable: true,
            },
          ]
        : []),
      {
        key: "zieltermin",
        label: "Fällig",
        align: "center",
        width: "15%",
        sortable: true,
      },
      {
        key: "responsible",
        label: "Verantwortlich",
        align: "left",
        width: "15%",
        sortable: true,
      },
      {
        key: "done",
        label: "Erledigt",
        align: "center",
        width: "10%",
        sortable: true,
      },
    ],
    [showReference],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData) => {
      const deadline = row as DeadlineItem & {
        reference: string;
        responsible: string;
      };
      switch (column.key) {
        case "bezeichnung":
          // Die Dringlichkeit steht nur als Icon direkt hinter der Bezeichnung – die Zeile
          // wird nicht eingefärbt.
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: fonts.weights.medium,
                  color: colors.text.primary,
                }}
              >
                {deadline.bezeichnung}
              </Typography>
              <NoteIndicator note={deadline.note} problem={deadline.problem} />
              <UrgencyIcon
                urgency={
                  isFurther(deadline)
                    ? "normal"
                    : getUrgency(deadline.zieltermin)
                }
              />
            </Box>
          );
        case "reference":
          return (
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {deadline.reference}
            </Typography>
          );
        case "zieltermin":
          return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DeadlineCell value={deadline.zieltermin} urgency="normal" />
            </Box>
          );
        case "responsible":
          return (
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {deadline.responsible || "–"}
            </Typography>
          );
        case "done":
          if (deadline.source === "derived") {
            return (
              <Tooltip
                arrow
                title="Ergibt sich aus dem Stand – erledigt sich mit dem nächsten Schritt"
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.secondary }}
                >
                  –
                </Typography>
              </Tooltip>
            );
          }
          return isDone(deadline) ? (
            <Tooltip
              arrow
              title={
                deadline.done_at
                  ? `Erledigt am ${formatDate(deadline.done_at)}`
                  : "Erledigt"
              }
            >
              <DoneIcon
                aria-label="Erledigt"
                sx={{ fontSize: "1.1rem", color: colors.status.success.main }}
              />
            </Tooltip>
          ) : null;
        default:
          return null;
      }
    },
    [isFurther],
  );

  const renderActions = useCallback(
    (row: TableData) => {
      const deadline = row as DeadlineItem;
      if (deadline.source !== "manual" || !canEdit) return null;
      return (
        <ActionsMenu
          ariaLabel={`Aktionen zu Termin ${deadline.bezeichnung}`}
          items={[
            // Kein „Bearbeiten“: Ein Klick auf die Zeile öffnet den Termin, dort bearbeitet man ihn.
            {
              label: "Löschen",
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => setDeleting(deadline),
              danger: true,
            },
          ]}
        />
      );
    },
    [canEdit],
  );

  // Eine manuelle Frist öffnet sich zum Lesen. Eine abgeleitete führt zu ihrem Bezug – außer er
  // ist die Seite, auf der man steht.
  const handleRowClick = useCallback(
    (row: TableData) => {
      const deadline = row as DeadlineItem;
      if (fieldIds.has(deadline.id)) {
        onFieldRowClick?.(deadline);
        return;
      }
      if (deadline.source === "manual") {
        setSelected(deadline);
        return;
      }
      if (isOnCurrentReference(deadline, { permitProcedureId, evidenceId }))
        return;
      const path = deadlinePath(deadline);
      if (path) navigate(path);
    },
    [navigate, evidenceId, permitProcedureId, fieldIds, onFieldRowClick],
  );

  /** Erledigen wirkt sofort, ohne Rückfrage – die Meldung bietet „Rückgängig“ */
  const handleSetDone = async (deadline: DeadlineItem, done: boolean) => {
    setSelected(null);
    if (await setDone(deadline.id, done)) {
      showUndo(done ? "Termin erledigt" : "Termin wieder geöffnet", () =>
        setDone(deadline.id, !done),
      );
    }
  };

  const getRowSx = useCallback(
    (row: TableData) => {
      const deadline = row as DeadlineItem;
      return isFurther(deadline) ? { opacity: 0.55 } : {};
    },
    [isFurther],
  );

  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {withScopeToggle && <ScopeToggle value={scope} onChange={setScope} />}
      {canEdit && (
        <Button
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          Termin anlegen
        </Button>
      )}
    </Box>
  );

  return (
    <Section
      title="Termine"
      noPadding
      noOuterPadding={noOuterPadding}
      actions={actions}
    >
      <BaseTable
        columns={columns}
        data={tableData}
        renderCell={renderCell}
        renderActions={canEdit ? renderActions : undefined}
        actionsColumnWidth="5%"
        onRowClick={handleRowClick}
        getRowSx={getRowSx}
        loading={loading}
        defaultSortColumn="zieltermin"
        disablePagination
        emptyMessage={
          furtherCount > 0 && !showFurther
            ? "Keine aktuellen Termine"
            : "Keine Termine"
        }
      />
      {furtherCount > 0 && (
        // Derselbe Pfeil wie in `ExpandableDetails` – hier unter der Tabelle, die bis an den
        // Kachelrand reicht.
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
          <Tooltip
            arrow
            title={
              showFurther
                ? "Nur aktuelle Termine"
                : `Weitere Termine (${furtherCount})`
            }
          >
            <IconButton
              size="small"
              aria-label={
                showFurther
                  ? "Weitere Termine ausblenden"
                  : "Weitere Termine anzeigen"
              }
              onClick={() => setShowFurther((prev) => !prev)}
              sx={{
                color: showFurther ? "primary.main" : "text.secondary",
                opacity: 0.5,
                p: 0.5,
              }}
            >
              {showFurther ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <ManualDeadlineDialog
        open={creating || !!selected}
        projectId={projectId}
        presetPermitProcedureId={permitProcedureId}
        presetEvidenceId={evidenceId}
        deadline={selected}
        canEdit={canEdit}
        showReferenceLink={
          !!selected &&
          !isOnCurrentReference(selected, { permitProcedureId, evidenceId })
        }
        onClose={() => {
          setCreating(false);
          setSelected(null);
        }}
        onSubmit={(submission) =>
          selected
            ? submission.kind === "project"
              ? update(selected.id, submission.data)
              : Promise.resolve(false)
            : submit(submission)
        }
        onSetDone={
          selected ? (done) => handleSetDone(selected, done) : undefined
        }
        onDelete={selected ? () => setDeleting(selected) : undefined}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Termin löschen"
        message={
          deleting ? `Termin „${deleting.bezeichnung}" wirklich löschen?` : ""
        }
        confirmLabel="Löschen"
        onConfirm={() => {
          if (deleting) remove(deleting.id);
          setDeleting(null);
          setSelected(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
};

export default DeadlinesSection;
